import React, { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link } from "react-router-dom";

import {
  ClassificationIcon,
  Grid,
  MoreItemsList,
  NoData,
  RenderWhen
} from "../../../../../../components";
import PreviewVisualizationModal from "../../../../../../components/PreviewVisualizationModal";
import Typography from "../../../../../../components/typography/Typography";
import useModal from "../../../../../../hooks/useModal";
import { getClassificationByLabels } from "../../../../../../lib/enums/ThreatStatusEnum";
import { route } from "../../../../../../shim/jsbridge";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { getSearchPivotLink, pathToSS } from "../../../../util";
import { useEmailAttachments } from "../../hooks";
import styles from "./email.module.scss";
import { useLocalSampleDetails } from "../../../../hooks";

export const getExtractedUrlLink = (type: string, value: string) => {
  const encodedValue = encodeURIComponent(value);
  if (type === "ipv4" || type === "ipv6") {
    return route("network_ip_summary", [encodedValue]);
  } else if (type === "url") {
    return route("network_url_summary", [encodedValue]);
  } else if (type === "domain") {
    return route("network_domain_summary", [encodedValue]);
  } else if (type === "mail") {
    return getSearchPivotLink(`email:${value}`);
  }
  return undefined;
};

const EmailAttachmentError = () => (
  <span className={commonStyles.error_msg}>
    Error loading email attachments
  </span>
);

const EmailAttachmentNoData = () => (
  <span className={commonStyles.error_msg}>No available email attachments</span>
);

type AttachmentData = {
  attachment_name: string;
  file_type: string;
  file_subtype: string;
  type_display: string;
  size: number;
  path: string | undefined;
  sha1: string;
  relationship_id: number | undefined;
  classification: string;
  classification_result: string;
  extracted_urls: { value: string; classification: string; type: string }[];
};

const EmailAttachmentGridSchema = (
  setCurrentPreviewData: React.Dispatch<object>,
  togglePreviewModal: () => void,
  sourceID?: number,
  hashValue?: string,
  relationshipID?: number
) => ({
  fields: [
    {
      name: "attachment_name",
      displayTitle: "Attachment Name",
      sortable: false,
      component: (data: AttachmentData) => (
        <Link
          to={pathToSS(data.sha1, sourceID, "", data.relationship_id)}
          className={styles.grid_element_with_classification}
        >
          <ClassificationIcon
            classification={data.classification}
            classificationStyle={{
              flexShrink: 0
            }}
          />
          <Typography
            variant={"body1"}
            className={`${styles.item_link} overflow-ellipsis`}
          >
            {" "}
            {data?.attachment_name}{" "}
          </Typography>
        </Link>
      ),
      style: { width: "30%" }
    },
    {
      name: "classification_reason",
      displayTitle: "Threat Name",
      sortable: false,
      component: (data: AttachmentData) => (
        <Typography variant={"body1"}>
          {data?.classification_result ?? "--"}
        </Typography>
      ),
      style: { width: "25%" }
    },
    {
      name: "extracted_urls",
      displayTitle: "Extracted URLs",
      sortable: false,
      component: (data: AttachmentData) => {
        if (!data.extracted_urls?.length) {
          return <NoData />;
        }

        const renderExtractedUrl = (item: unknown) => {
          const urlItem = item as {
            value: string;
            classification: string;
            type: string;
          };
          const linkTo = getExtractedUrlLink(urlItem.type, urlItem.value);
          return (
            <Link
              to={linkTo}
              className={styles.grid_element_with_classification}
            >
              <ClassificationIcon
                classification={urlItem.classification}
                classificationStyle={{
                  flexShrink: 0
                }}
              />
              <Typography
                variant={"body1"}
                className={`${styles.item_link} overflow-ellipsis`}
              >
                {urlItem.value}
              </Typography>
            </Link>
          );
        };

        return (
          <MoreItemsList
            showNumber={1}
            items={data.extracted_urls}
            renderItem={renderExtractedUrl}
            moreDisplayMode="link"
            moreLink={pathToSS(
              hashValue,
              sourceID,
              "network_references",
              relationshipID
            )}
          />
        );
      },
      style: { width: "30%" }
    },
    {
      name: "preview",
      sortable: false,
      component: (data: AttachmentData) => {
        if (!data) return <NoData />;
        return (
          <a
            onClick={() => {
              setCurrentPreviewData({
                sha1: data.sha1,
                sourceId: sourceID,
                relationshipId: data.relationship_id,
                fileType: data.file_type,
                fileSubtype: data.file_subtype,
                typeDisplay: data.type_display,
                threatStatus: getClassificationByLabels(data.classification)
              });
              togglePreviewModal();
            }}
          >
            <Typography
              variant={"body1"}
              className={`${styles.item_link} overflow-ellipsis`}
            >
              Preview
            </Typography>
          </a>
        );
      },
      style: { width: "15%", textAlign: "center" }
    }
  ]
});

interface EmailAttachmentsProps {
  hashValue: string;
  sourceID: number | undefined;
  relationshipID: number;
  attachmentsRoute: string;
}

const EmailAttachmentsGrid: React.FC<EmailAttachmentsProps> = ({
  hashValue,
  sourceID,
  attachmentsRoute,
  relationshipID
}) => {
  const { data: localData } = useLocalSampleDetails(
    hashValue,
    sourceID,
    relationshipID
  );
  const sourceHash = localData?.sample_source_sha1;
  const { results } = useEmailAttachments(
    sourceHash || hashValue,
    relationshipID
  );
  const [previewModalShow, , togglePreviewModal] = useModal();
  const [currentPreviewData, setCurrentPreviewData] = useState({
    sha1: "",
    sourceId: 0,
    relationshipId: undefined,
    fileType: "",
    fileSubtype: "",
    typeDisplay: "",
    threatStatus: 0
  });

  return (
    <>
      <ErrorBoundary fallback={<EmailAttachmentError />}>
        <div className={styles.attachments_grid}>
          <Grid
            schema={EmailAttachmentGridSchema(
              setCurrentPreviewData,
              togglePreviewModal,
              sourceID,
              hashValue,
              relationshipID
            )}
            data={results.slice(0, 4)}
            emptyGridMsg={<EmailAttachmentNoData />}
            showLoading={false}
            hidePaging
          />
          {results?.length > 4 && (
            <Typography variant={"body1"} className={styles.more_attachments}>
              <Link className={commonStyles.link} to={attachmentsRoute}>
                Show More
              </Link>
            </Typography>
          )}
        </div>
      </ErrorBoundary>
      <RenderWhen condition={previewModalShow}>
        <PreviewVisualizationModal
          sha1={currentPreviewData.sha1}
          fileType={currentPreviewData.fileType}
          fileSubtype={currentPreviewData.fileSubtype}
          typeDisplay={currentPreviewData.typeDisplay}
          sourceId={currentPreviewData.sourceId}
          threatStatus={currentPreviewData.threatStatus}
          relationshipId={currentPreviewData.relationshipId}
          onHide={togglePreviewModal}
        />
      </RenderWhen>
    </>
  );
};

export default EmailAttachmentsGrid;
